import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  TextField,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../../api/axios";
import { useApiRequest } from "../../../api/axios";
import { SETTLEMENTS_NEEDS_REMINDER_ENDPOINT, SETTLEMENTS_SEND_REMINDERS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR, TENANT_SETTINGS_ENDPOINT, getSettlementUpdateClientEmailEndpoint } from "../../../consts";
import { Settlement } from "../../../types/settlements";
import {
  getSettlementClientName,
  getSettlementContractInternalName,
  getSettlementContractNumber,
  getSettlementEquipmentLabel,
} from "../../../utils/settlementContractTerms";
import CustomDataGrid from "../../ui/customDataGrid/customDataGrid.component";
import { useCRUD } from "../../../api/crud";
import styles from "./sendReminder.module.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_SETTLEMENTS_PAGE_SIZE = 25;

type EmailUpdateScope = "equipment" | "client" | "contract" | "all_contracts";

const EMAIL_SCOPE_OPTIONS: { value: EmailUpdateScope; labelKey: string }[] = [
  { value: "equipment", labelKey: "This Equipment" },
  { value: "client", labelKey: "This Client" },
  { value: "contract", labelKey: "All Equipments in this contract" },
  { value: "all_contracts", labelKey: "All Contracts" },
];

interface SendReminderProps {
  onSuccess?: () => void;
  preselectedSettlementId?: string;
  preselectedContractId?: string;
}

interface ReminderSettlementRow {
  id: string;
  settlement: Settlement;
  equipmentName: string;
  contractNumber: string;
  contractInternalName: string;
  clientName: string;
  email: string;
  hasEmail: boolean;
}

export const SendReminder = ({ onSuccess, preselectedSettlementId, preselectedContractId }: SendReminderProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, executeRequest } = useApiRequest();
  const { get: getTenantSettings, loading: loadingSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadingSettlements, setLoadingSettlements] = useState<boolean>(false);
  const [settlementsPage, setSettlementsPage] = useState<number>(1);
  const [settlementsPageSize, setSettlementsPageSize] = useState<number>(DEFAULT_SETTLEMENTS_PAGE_SIZE);
  const [totalSettlementsCount, setTotalSettlementsCount] = useState<number>(0);
  const [reminderTitle, setReminderTitle] = useState<string>("");
  const [reminderMessage, setReminderMessage] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortField, setSortField] = useState<string>("contract");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const getSortParam = useCallback((field: string, direction: "asc" | "desc") => {
    const mapping: Record<string, string> = {
      contract: "contract_number",
      client: "client_name",
      equipment: "equipment",
      email: "email",
    };

    const normalizedField = mapping[field] || field;
    return direction === "desc" ? `-${normalizedField}` : normalizedField;
  }, []);
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const editingEmailValueRef = useRef<string>("");
  const [editingEmailScope, setEditingEmailScope] = useState<EmailUpdateScope>("equipment");
  const [savingEmail, setSavingEmail] = useState<boolean>(false);
  const [confirmSendAllOpen, setConfirmSendAllOpen] = useState<boolean>(false);
  // Also lists this month's settlements in any status and ignores the reminder
  // cooldown - the backend applies the same wider rule when sending.
  const [showAll, setShowAll] = useState<boolean>(false);
  const latestFetchIdRef = useRef<number>(0);

  const fetchSettlements = useCallback(
    async ({
      page = 1,
      query = "",
      pageSize = settlementsPageSize,
    }: { page?: number; query?: string; pageSize?: number } = {}) => {
      const requestId = latestFetchIdRef.current + 1;
      latestFetchIdRef.current = requestId;

      setLoadingSettlements(true);

      try {
        const response = await axios.get(SETTLEMENTS_NEEDS_REMINDER_ENDPOINT, {
          params: {
            page,
            page_size: pageSize,
            ...(query.trim() ? { search: query.trim() } : {}),
            ...(sortField ? { ordering: getSortParam(sortField, sortDirection) } : {}),
            ...(preselectedSettlementId ? { settlement_id: preselectedSettlementId } : {}),
            ...(preselectedContractId ? { contract_id: preselectedContractId } : {}),
            ...(showAll ? { show_all: true } : {}),
          },
        });

        if (latestFetchIdRef.current !== requestId) {
          return;
        }

        const payload = response.data;
        const fetchedSettlements = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload)
          ? payload
          : [];

        setSettlements(fetchedSettlements);
        setSettlementsPage(page);
        setTotalSettlementsCount(
          Array.isArray(payload)
            ? fetchedSettlements.length
            : Number(payload?.count) || fetchedSettlements.length
        );

        if (preselectedSettlementId) {
          const exists = fetchedSettlements.some((s: Settlement) => s.id === preselectedSettlementId);
          if (exists) {
            setSelectedIds((prev) => {
              const updated = new Set(prev);
              updated.add(preselectedSettlementId);
              return updated;
            });
          }
        } else if (preselectedContractId) {
          // All fetched settlements already belong to this contract (server-side
          // filtered), so select every one of them by default.
          setSelectedIds((prev) => {
            const updated = new Set(prev);
            fetchedSettlements.forEach((s: Settlement) => updated.add(s.id));
            return updated;
          });
        }
      } catch (error) {
        enqueueSnackbar(t("Failed to fetch data"), ERROR_SNACKBAR);
      } finally {
        if (latestFetchIdRef.current === requestId) {
          setLoadingSettlements(false);
        }
      }
    },
    [
      enqueueSnackbar,
      getSortParam,
      preselectedContractId,
      preselectedSettlementId,
      settlementsPageSize,
      showAll,
      sortDirection,
      sortField,
      t,
    ]
  );

  const handleShowAllChange = (checked: boolean) => {
    setShowAll(checked);
    setSettlementsPage(1);
    // Rows only "Show all" lists would be skipped by a send from the default
    // view, so switching it off starts the selection over.
    if (!checked) {
      setSelectedIds(new Set());
    }
  };

  useEffect(() => {
    const fetchReminderSettings = async () => {
      try {
        const settings = await getTenantSettings("settings");
        if (settings?.data) {
          setReminderTitle(settings.data.reminder_template_title || "");
          setReminderMessage(settings.data.reminder_template_message || "");
        }
      } catch (error) {
        enqueueSnackbar(t("Failed to fetch data"), ERROR_SNACKBAR);
      }
    };

    fetchReminderSettings();
  }, [enqueueSnackbar, t, getTenantSettings]);

  const quickFilterValue = gridFilterModel.quickFilterValues?.[0];
  const pendingSearchQuery = typeof quickFilterValue === "string" ? quickFilterValue.trim() : "";
  // "Send to all" sends searchQuery, which trails the search box by the debounce
  // below. Until it catches up (and the grid refetches), the count on screen and
  // the recipients the backend would resolve can disagree, so hold the action.
  const sendToAllBlocked =
    loading || loadingSettlements || loadingSettings || pendingSearchQuery !== searchQuery;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchQuery(pendingSearchQuery);
      setSettlementsPage(1);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [pendingSearchQuery]);

  useEffect(() => {
    setSettlementsPage(1);
  }, [settlementsPageSize]);

  useEffect(() => {
    fetchSettlements({ page: settlementsPage, query: searchQuery, pageSize: settlementsPageSize });
  }, [fetchSettlements, searchQuery, settlementsPage, settlementsPageSize]);

  useEffect(() => {
    // Keep the selection in sync with reality: if a visible settlement lost its
    // reminder email (e.g. it was just cleared), it can no longer be a recipient.
    setSelectedIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }

      let changed = false;
      const updated = new Set(prev);
      settlements.forEach((settlement) => {
        const hasEmail = Boolean(
          (settlement as Settlement & { client_email?: string }).client_email
        );
        if (!hasEmail && updated.delete(settlement.id)) {
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [settlements]);

  const handleToggle = useCallback((settlementId: string) => {
    const targetSettlement = settlements.find((s) => s.id === settlementId);
    const hasEmail = Boolean((targetSettlement as Settlement & { client_email?: string })?.client_email);

    if (!hasEmail) {
      enqueueSnackbar(t("Add an email before selecting this settlement"), ERROR_SNACKBAR);
      return;
    }

    setSelectedIds((prev) => {
      const updated = new Set(prev);

      if (updated.has(settlementId)) {
        updated.delete(settlementId);
      } else {
        updated.add(settlementId);
      }

      return updated;
    });
  }, [enqueueSnackbar, settlements, t]);

  const validateReminderTemplate = (): boolean => {
    if (!reminderTitle.trim() || !reminderMessage.trim()) {
      enqueueSnackbar(t("Please fill in both title and message"), ERROR_SNACKBAR);
      return false;
    }

    return true;
  };

  const handleOpenSendAllConfirm = () => {
    if (validateReminderTemplate()) {
      setConfirmSendAllOpen(true);
    }
  };

  const handleSendReminders = async ({ sendToAll = false }: { sendToAll?: boolean } = {}) => {
    if (!sendToAll && selectedIds.size === 0) {
      enqueueSnackbar(t("Please select at least one settlement"), ERROR_SNACKBAR);
      return;
    }

    if (!validateReminderTemplate()) {
      return;
    }

    // "Send to all" lets the backend resolve the recipients from the same
    // filters the grid uses, so it reaches every matching settlement - not
    // just the page currently loaded here.
    const payload = sendToAll
      ? {
          select_all: true,
          search: searchQuery,
          contract_id: preselectedContractId || null,
          show_all: showAll,
          title: reminderTitle.trim(),
          message: reminderMessage.trim(),
        }
      : {
          settlement_ids: Array.from(selectedIds),
          show_all: showAll,
          title: reminderTitle.trim(),
          message: reminderMessage.trim(),
        };

    setConfirmSendAllOpen(false);

    const result = await executeRequest(() =>
      axios.post(SETTLEMENTS_SEND_REMINDERS_ENDPOINT, payload)
    );

    if (result?.success) {
      enqueueSnackbar(
        t("Reminders sent successfully"),
        SUCCESS_SNACKBAR
      );
      if (onSuccess) {
        onSuccess();
      }
    } else if (result?.errorData?.code === "no_reminder_recipients") {
      enqueueSnackbar(t("None of these settlements has an email address to send a reminder to"), ERROR_SNACKBAR);
    } else if (result?.errorData?.code === "demo_mode_restricted") {
      // Names the addresses a demo workspace may not write to (B-25.3).
      enqueueSnackbar(result.errorData.message, ERROR_SNACKBAR);
    } else {
      enqueueSnackbar(t("Failed to send reminders"), ERROR_SNACKBAR);
    }
  };

  const getClientEmail = useCallback((settlement: Settlement & { client_email?: string }): string => {
    return settlement.client_email || t("No email available");
  }, [t]);

  const settlementRows = useMemo(() => {
    return settlements.map((settlement) => {
      const equipmentName = getSettlementEquipmentLabel(settlement, "");
      const contractNumber = getSettlementContractNumber(settlement);
      const contractInternalName = getSettlementContractInternalName(settlement);
      const clientName = getSettlementClientName(settlement);
      const hasEmail = Boolean((settlement as Settlement & { client_email?: string }).client_email);
      const email = getClientEmail(settlement);

      return {
        settlement,
        equipmentName,
        contractNumber,
        contractInternalName,
        clientName,
        email,
        hasEmail,
      };
    });
  }, [settlements, getClientEmail]);

  const gridRows = useMemo<ReminderSettlementRow[]>(
    () =>
      settlementRows.map((row) => ({
        id: row.settlement.id,
        settlement: row.settlement,
        equipmentName: row.equipmentName || "-",
        contractNumber: row.contractNumber || "-",
        contractInternalName: row.contractInternalName || "-",
        clientName: row.clientName || "-",
        email: row.email || "-",
        hasEmail: row.hasEmail,
      })),
    [settlementRows]
  );

  const visibleSettlementIds = gridRows.map((row) => row.id);
  const visibleSelectedCount = visibleSettlementIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleSettlementIds.length > 0 && visibleSelectedCount === visibleSettlementIds.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const handleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const updated = new Set(prev);
      const eligibleVisibleIds = visibleSettlementIds.filter((id) => {
        const settlement = settlements.find((item) => item.id === id);
        return Boolean((settlement as Settlement & { client_email?: string })?.client_email);
      });

      if (allVisibleSelected) {
        visibleSettlementIds.forEach((id) => updated.delete(id));
      } else {
        eligibleVisibleIds.forEach((id) => updated.add(id));
      }

      return updated;
    });
  }, [allVisibleSelected, settlements, visibleSettlementIds]);

  const settlementsPaginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: Math.max(settlementsPage - 1, 0),
      pageSize: settlementsPageSize,
    }),
    [settlementsPage, settlementsPageSize]
  );

  const settlementsSortModel = useMemo<GridSortModel>(
    () => (sortField ? [{ field: sortField, sort: sortDirection }] : []),
    [sortDirection, sortField]
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.pageSize !== settlementsPageSize) {
        setSettlementsPageSize(model.pageSize);
        setSettlementsPage(1);
        return;
      }

      setSettlementsPage(model.page + 1);
    },
    [settlementsPageSize]
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    const nextSort = model[0];

    // The grid cycles asc -> desc -> unsorted and emits an empty model on that
    // third click. Clearing the sort here is what every other table does; this
    // one used to ignore the empty model, which left the controlled sortModel
    // pinned to desc and made the column header look dead from then on.
    if (!nextSort?.field || !nextSort.sort) {
      setSortField("");
      setSettlementsPage(1);
      return;
    }

    const nextField = String(nextSort.field);
    const nextDirection = nextSort.sort === "desc" ? "desc" : "asc";

    if (nextField !== sortField || nextDirection !== sortDirection) {
      setSortField(nextField);
      setSortDirection(nextDirection);
      setSettlementsPage(1);
    }
  }, [sortDirection, sortField]);

  const handleEditEmail = useCallback((settlementId: string, currentEmail: string) => {
    const nextValue = currentEmail !== t("No email available") ? currentEmail : "";
    setEditingEmailId(settlementId);
    setEditingEmailScope("equipment");
    editingEmailValueRef.current = nextValue;
  }, [t]);

  const handleCancelEmailEdit = useCallback(() => {
    setEditingEmailId(null);
    setEditingEmailScope("equipment");
    editingEmailValueRef.current = "";
  }, []);

  const stopCellEventPropagation = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const handleSaveEmail = useCallback(async (settlementId: string, scope: EmailUpdateScope) => {
    const sanitizedEmail = editingEmailValueRef.current.trim();

    // An empty value is allowed: it clears the stored email (set to null).
    if (sanitizedEmail && !EMAIL_REGEX.test(sanitizedEmail)) {
      enqueueSnackbar(t("Please enter a valid email address"), ERROR_SNACKBAR);
      return;
    }

    setSavingEmail(true);
    try {
      const response = await axios.patch(
        getSettlementUpdateClientEmailEndpoint(settlementId),
        { email: sanitizedEmail || null, scope }
      );
      if (response.status === 200) {
        enqueueSnackbar(
          sanitizedEmail ? t("Email updated successfully") : t("Email cleared successfully"),
          SUCCESS_SNACKBAR
        );
        setEditingEmailId(null);
        setEditingEmailScope("equipment");
        editingEmailValueRef.current = "";
        // The set of affected rows depends on the chosen scope and on the
        // backend's own recipient-resolution rules, so refetch instead of
        // patching local state row by row.
        await fetchSettlements({
          page: settlementsPage,
          query: searchQuery,
          pageSize: settlementsPageSize,
        });
      }
    } catch {
      enqueueSnackbar(t("Failed to update email"), ERROR_SNACKBAR);
    } finally {
      setSavingEmail(false);
    }
  }, [enqueueSnackbar, fetchSettlements, searchQuery, settlementsPage, settlementsPageSize, t]);

  const reminderTagsHelperText = (
    <>
      {t("Reminder templates support placeholders that are replaced with actual values when sending.")}
      <br />
      <strong>{t("Available tags")}:</strong>
      <br />
      {"{"}CLIENT_NAME{"}"} - {t("client name")}
      <br />
      {"{"}EQUIPMENTS_DETAILS{"}"} - {t("full equipment details (name and serial, bullet list)")}
      <br />
      {"{"}EQUIPMENTS_NAMES{"}"} - {t("equipment names (bullet list)")}
      <br />
      {"{"}EQUIPMENTS_SERIALS{"}"} - {t("equipment serial numbers (bullet list)")}
      <br />
      {"{"}EQUIPMENTS_ADDRESSES{"}"} - {t("equipment installation addresses (bullet list)")}
      <br />
      {"{"}COMPANY_NAME{"}"} - {t("workspace or company name")}
      <br />
      {"{"}COMPANY_LOGO{"}"} - {t("company logo (message only)")}
    </>
  );

  const NoRowsOverlay = useCallback(
    () => (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {searchQuery
            ? t("No settlements match current filters.")
            : showAll
            ? t("No settlements this month.")
            : t("No settlements need reminders at this time.")}
        </Typography>
      </Box>
    ),
    [searchQuery, showAll, t]
  );

  const gridColumns = useMemo<GridColDef<ReminderSettlementRow>[]>(() => {
    return [
      {
        field: "selection",
        headerName: t("Select"),
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        renderHeader: () => (
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected}
            onChange={handleSelectAllVisible}
            onClick={stopCellEventPropagation}
            disabled={loading || visibleSettlementIds.length === 0}
            inputProps={{ "aria-label": t("Select all") }}
          />
        ),
        renderCell: (params) => {
          const canSelect = !loading && params.row.hasEmail;

          return (
            <Checkbox
              checked={selectedIds.has(params.row.id)}
              // Only wire up onChange when selection is actually allowed. The mobile
              // list renders this checkbox outside a MUI DataGrid cell, where a
              // disabled input can still emit a change on tap and would otherwise
              // fire a confusing "add an email" toast on every tap.
              onChange={canSelect ? () => handleToggle(params.row.id) : undefined}
              onClick={stopCellEventPropagation}
              disabled={!canSelect}
            />
          );
        },
      },
      {
        field: "contract",
        headerName: t("Number"),
        valueGetter: (_value, row) => row.contractNumber,
      },
      {
        field: "internal_name",
        headerName: t("Internal name"),
        valueGetter: (_value, row) => row.contractInternalName,
      },
      {
        field: "client",
        headerName: t("Client"),
        valueGetter: (_value, row) => row.clientName,
      },
      {
        field: "equipment",
        headerName: t("Equipment"),
        valueGetter: (_value, row) => row.equipmentName,
      },
      {
        field: "email",
        headerName: t("Email"),
        valueGetter: (_value, row) => row.email,
        renderCell: (params) => {
          const settlementId = params.row.id;

          if (editingEmailId === settlementId) {
            return (
              <div className={styles.emailEditContainer} onClick={stopCellEventPropagation}>
                <TextField
                  key={settlementId}
                  size="small"
                  type="email"
                  defaultValue={editingEmailValueRef.current}
                  onChange={(event) => {
                    editingEmailValueRef.current = event.target.value;
                  }}
                  disabled={savingEmail}
                  autoFocus
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === "Escape") {
                      handleCancelEmailEdit();
                    }
                  }}
                  helperText={t("Leave empty to clear the email")}
                  sx={{ minWidth: 180 }}
                />
                <div className={styles.emailEditActions}>
                  <TextField
                    select
                    size="small"
                    label={t("Apply to")}
                    value={editingEmailScope}
                    onChange={(event) =>
                      setEditingEmailScope(event.target.value as EmailUpdateScope)
                    }
                    onClick={stopCellEventPropagation}
                    disabled={savingEmail}
                    sx={{ minWidth: 220 }}
                  >
                    {EMAIL_SCOPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleSaveEmail(settlementId, editingEmailScope)}
                    disabled={savingEmail}
                  >
                    {t("Apply")}
                  </Button>
                  <Button
                    size="small"
                    onClick={handleCancelEmailEdit}
                    disabled={savingEmail}
                  >
                    {t("Cancel")}
                  </Button>
                </div>
              </div>
            );
          }

          return (
            <div className={styles.emailCell} onClick={stopCellEventPropagation}>
              <span
                className={`${styles.emailText} ${!params.row.hasEmail ? styles.emailTextDisabled : ""}`}
              >
                {params.row.email || "-"}
              </span>
              <Tooltip title={t("Edit email")}>
                <IconButton
                  size="small"
                  onClick={() => handleEditEmail(settlementId, params.row.email)}
                  disabled={loading}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ];
  }, [
    allVisibleSelected,
    editingEmailId,
    editingEmailScope,
    handleCancelEmailEdit,
    handleEditEmail,
    handleSaveEmail,
    handleSelectAllVisible,
    handleToggle,
    loading,
    savingEmail,
    selectedIds,
    someVisibleSelected,
    stopCellEventPropagation,
    t,
    visibleSettlementIds.length,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("Select settlements to send reminders for outdated counters")}
        </Typography>
        <Tooltip
          title={t(
            "Also list this month's settlements that are already confirmed or have up-to-date counters, and those reminded recently"
          )}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showAll}
                onChange={(event) => handleShowAllChange(event.target.checked)}
                disabled={loading}
              />
            }
            label={t("Show all")}
            sx={{ mr: 0, flexShrink: 0 }}
          />
        </Tooltip>
      </Box>

      <CustomDataGrid
        url={SETTLEMENTS_NEEDS_REMINDER_ENDPOINT}
        gridSettingsKey="SETTLEMENTS_SEND_REMINDERS"
        disableServerQuery
        columns={gridColumns}
        dataGridProps={{
          rows: gridRows,
          rowCount: totalSettlementsCount,
          loading: loadingSettlements,
          paginationModel: settlementsPaginationModel,
          sortModel: settlementsSortModel,
          filterModel: gridFilterModel,
          paginationMode: "server",
          sortingMode: "server",
          filterMode: "server",
          onPaginationModelChange: handlePaginationModelChange,
          onSortModelChange: handleSortModelChange,
          onFilterModelChange: setGridFilterModel,
          // Report keystrokes straight away: the debounce lives here, where
          // "Send to all" can see the search is still pending.
          filterDebounceMs: 0,
          disableRowSelectionOnClick: true,
          disableColumnFilter: true,
          disableColumnSelector: false,
          showToolbar: true,
          hideFooterSelectedRowCount: true,
          getRowHeight: () => "auto",
          slotProps: {
            toolbar: {
              showQuickFilter: true,
            },
          },
          sx: {
            minHeight: 320,
          },
          slots: {
            noRowsOverlay: NoRowsOverlay,
          },
        }}
      />

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t("Reminder message")}
        </Typography>
        <TextField
          label={t("Title")}
          value={reminderTitle}
          onChange={(e) => setReminderTitle(e.target.value)}
          size="small"
          fullWidth
          disabled={loading || loadingSettings}
          required
          helperText={t("Tag details are listed below. You can use the same tags in the title.")}
        />
        <TextField
          label={t("Message")}
          value={reminderMessage}
          onChange={(e) => setReminderMessage(e.target.value)}
          multiline
          rows={6}
          size="small"
          fullWidth
          disabled={loading || loadingSettings}
          required
          helperText={reminderTagsHelperText}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
        <Button
          variant="contained"
          onClick={() => handleSendReminders()}
          disabled={loading || selectedIds.size === 0 || loadingSettlements || loadingSettings}
          fullWidth
        >
          {loading
            ? t("Sending...")
            : t("Send reminders ({{count}} selected)", {
                count: selectedIds.size,
              })}
        </Button>
        {!preselectedSettlementId && (
          <Button
            variant="outlined"
            onClick={handleOpenSendAllConfirm}
            disabled={sendToAllBlocked || totalSettlementsCount === 0}
            fullWidth
          >
            {searchQuery
              ? t("Send reminders to all matching ({{count}})", { count: totalSettlementsCount })
              : t("Send reminders to all ({{count}})", { count: totalSettlementsCount })}
          </Button>
        )}
      </Box>

      <Dialog open={confirmSendAllOpen} onClose={() => setConfirmSendAllOpen(false)}>
        <DialogTitle>{t("Send reminders to all settlements?")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {searchQuery
              ? t("Reminders will be sent for all {{count}} settlements matching the current search, including those not visible on this page.", { count: totalSettlementsCount })
              : showAll
              ? t("Reminders will be sent for all {{count}} settlements on the list, including those not visible on this page.", { count: totalSettlementsCount })
              : t("Reminders will be sent for all {{count}} settlements awaiting a reminder, including those not visible on this page.", { count: totalSettlementsCount })}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            {t("Settlements without an email address will be skipped.")}
          </DialogContentText>
          {showAll ? (
            <DialogContentText sx={{ mt: 1 }}>
              {t("\"Show all\" ignores the waiting time between reminders, so clients reminded recently will get another one.")}
            </DialogContentText>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendAllOpen(false)} disabled={loading}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSendReminders({ sendToAll: true })}
            disabled={sendToAllBlocked}
          >
            {t("Send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
